import axios, { AxiosRequestConfig } from 'axios';
import { ToastService } from './ToastService';
import type { Instance } from 'mobx-state-tree';

// Get the backend URL from environment variables, with a fallback for local development
console.log(`[api.ts] VITE_BACKEND_URL from env: ${import.meta.env.VITE_BACKEND_URL}`);
const baseURL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:4011';
console.log(`[api.ts] Axios baseURL set to: ${baseURL}`);

let appStore: any; // Use any to avoid circular dependency issues

export const setAppStore = (store: any) => {
  appStore = store;
};

/**
 * Extended axios config with optional toast message and retry flag
 */
export interface ApiRequestConfig extends AxiosRequestConfig {
  /**
   * Optional toast message to show on success.
   * If provided, a success toast will be shown with this message.
   */
  toastMessage?: string;
  /**
   * Internal flag to track if this is a retry request
   */
  _retry?: boolean;
}

/**
 * The universal axios instance for all backend communication.
 *
 * It is pre-configured with:
 * - The correct backend baseURL.
 * - `withCredentials: true` to automatically send httpOnly cookies.
 * - Success/error toast notifications when toastMessage is provided
 */
const api = axios.create({
  baseURL: baseURL,
  withCredentials: true,
});

// Global variable to store current Stytch JWT (set by StytchSessionSync)
let currentStytchJWT: string | null = null;

export const setCurrentStytchJWT = (jwt: string | null) => {
  currentStytchJWT = jwt;
  console.log(`🔑 [API] Stytch JWT ${jwt ? 'set' : 'cleared'} globally`);
};

/**
 * Get the current Stytch JWT for manual use in fetch calls (e.g., streaming)
 * @returns Current JWT or null if not set
 */
export const getCurrentStytchJWT = (): string | null => {
  return currentStytchJWT;
};

// Request interceptor - Add Stytch session JWT to Authorization header
api.interceptors.request.use(
  (config) => {
    console.log(`🔍 [API] ════════════════════════════════════════════════`);
    console.log(`🔍 [API] REQUEST to: ${config.url}`);
    console.log(`🔍 [API] Method: ${config.method?.toUpperCase()}`);
    console.log(`🔍 [API] Full URL: ${config.baseURL}${config.url}`);
    
    // PRIORITY 1: Use globally stored JWT (set by StytchSessionSync)
    if (currentStytchJWT) {
      config.headers.Authorization = `Bearer ${currentStytchJWT}`;
      console.log('✅ [API] Using globally stored Stytch JWT in Authorization header');
      console.log(`✅ [API] JWT (first 50 chars): ${currentStytchJWT.substring(0, 50)}...`);
      console.log(`✅ [API] Authorization header SET: Bearer ${currentStytchJWT.substring(0, 30)}...`);
      return config;
    }
    
    // PRIORITY 2: Try to extract from localStorage (legacy/fallback)
    let sessionJWT = null;
    
    // Look for Stytch session in localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.includes('stytch') && key.includes('session')) {
        try {
          const value = localStorage.getItem(key);
          if (!value) continue;
          
          // Try to parse as JSON first
          try {
            const parsed = JSON.parse(value);
            // Look for session_jwt in various possible locations
            sessionJWT = parsed.session_jwt || 
                        parsed.member_session?.session_jwt ||
                        parsed.session?.session_jwt ||
                        null;
            
            if (sessionJWT) {
              console.log(`🔑 [API] Found Stytch JWT in localStorage key: ${key}`);
              break;
            }
          } catch {
            // If it's not JSON, check if it's a raw JWT
            if (value.startsWith('eyJ')) {
              sessionJWT = value;
              console.log(`🔑 [API] Found raw JWT in localStorage key: ${key}`);
              break;
            }
          }
        } catch (e) {
          // Skip this key
        }
      }
    }
    
    // PRIORITY 3: Try to extract JWT from cookies (in case it's not httpOnly)
    if (!sessionJWT) {
      const cookies = document.cookie.split('; ');
      const stytchSessionCookie = cookies.find(c => c.startsWith('stytch_session_jwt='));
      if (stytchSessionCookie) {
        sessionJWT = stytchSessionCookie.split('=')[1];
        console.log('🔑 [API] Found Stytch JWT in cookie (extracted)');
      }
    }
    
    if (sessionJWT) {
      config.headers.Authorization = `Bearer ${sessionJWT}`;
      console.log('✅ [API] Stytch session JWT added to Authorization header from localStorage/cookie');
    } else {
      // NO JWT FOUND - this will cause 401 errors!
      console.warn('⚠️ [API] NO JWT FOUND - Request will likely fail with 401!');
      console.warn('   Checked: Global variable, localStorage, cookies');
      console.warn('   This means Stytch session is not accessible');
    }
    
    // Cookies are still sent via withCredentials
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token refresh logic and toasts
api.interceptors.response.use(
  (response) => {
    // Show success toast if toastMessage was provided
    const config = response.config as ApiRequestConfig;
    if (config.toastMessage) {
      ToastService.success(config.toastMessage);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config as ApiRequestConfig;

    // Show error toast if toastMessage was provided
    if (originalRequest.toastMessage) {
      const errorMsg = error.response?.data?.message || error.message || 'Operation failed';
      ToastService.error(`${originalRequest.toastMessage} failed: ${errorMsg}`);
    }

    // Check if the error is a 401 and if it's not a retry request
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip 401 handling entirely for auth endpoints
      const url = originalRequest.url || '';
      console.log('🔍 [API] Got 401 error for URL:', url);
      if (url.includes('/auth/')) {
        console.log('⏭️ [API] 401 on auth endpoint, skipping token refresh logic - NO REDIRECT!');
        return Promise.reject(error);
      }
      console.log('⚠️ [API] 401 on non-auth endpoint, will attempt refresh...');
      
      originalRequest._retry = true; // Mark this request as a retry

      try {
        console.log('Token expired, attempting to refresh...');
        
        // Use the same api instance to send the refresh token cookie
        await api.post('/auth/refresh');
        
        console.log('Token refreshed successfully, retrying original request...');
        // Retry the original request with the new session
        return api(originalRequest);

      } catch (refreshError) {
        console.error('Failed to refresh token:', refreshError);
        
        // Dynamically import the store to avoid circular dependencies
        // const { appStore } = await import('../stores/AppStore'); // This line is removed as appStore is now global
        appStore.auth.signOut(); // Trigger a full sign-out
        
        // Redirect to login page ONLY if not already on an auth page
        const currentPath = window.location.pathname;
        if (!currentPath.includes('/login') && !currentPath.includes('/signup') && !currentPath.includes('/auth/')) {
          console.log('🔄 [API] Redirecting to login due to auth failure');
          window.location.href = '/login';
        } else {
          console.log('⏭️ [API] Already on auth page, skipping redirect');
        }
        
        return Promise.reject(refreshError);
      }
    }

    // For all other errors, just pass them through
    return Promise.reject(error);
  }
);

export default api;
